import React, { useState } from 'react';
import { useTimesheets, timesheetsAPI, useEmployees } from '@/api';
import { useBranchScope } from '@/context/BranchScopeContext';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge } from '@/components/ui';
import { LoadingSpinner, Pagination } from '@/components/common';
import { Calendar, Clock, Download, RefreshCw, Search, UserCheck, UserX, AlertTriangle, CheckCircle2, Activity, ShieldCheck, X } from 'lucide-react';
import { formatAttendanceTime } from '../utils/datetime';
import { extractPaginatedData } from '@/utils/pagination';
import { toast } from 'react-toastify';

// Helper to get today's date in YYYY-MM-DD format
const getTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function TimesheetsPage() {
  const perPage = 20;
  const [page, setPage] = useState(1);
  // Default to today's date for both filters
  const today = getTodayISO();
  const [filters, setFilters] = useState({
    employee_id: '',
    date_from: today,
    date_to: today,
  });
  const [recomputing, setRecomputing] = useState(false);
  const { branchParam } = useBranchScope();
  const { data: timesheetsData, isLoading, refetch } = useTimesheets({
    ...filters,
    branch_id: branchParam,
    page,
    per_page: perPage,
  });
  const { items: timesheets, meta } = extractPaginatedData<any>(timesheetsData, page, perPage);
  const { data: employeesData } = useEmployees({ is_active: true, per_page: 500 });
  const { items: employees } = extractPaginatedData<any>(employeesData, 1, 500);

  // Excuse modal state
  const [excuseModal, setExcuseModal] = useState<{ open: boolean; timesheet: any | null }>({ open: false, timesheet: null });
  const [excuseForm, setExcuseForm] = useState({ excused_until: '', excuse_reason: '' });
  const [excuseLoading, setExcuseLoading] = useState(false);
  const [manualModal, setManualModal] = useState<{ open: boolean; timesheet: any | null }>({ open: false, timesheet: null });
  const [manualForm, setManualForm] = useState({ check_in_at: '', check_out_at: '', reason: '' });
  const [manualLoading, setManualLoading] = useState(false);

  const handleSetExcuse = async () => {
    if (!excuseModal.timesheet) return;
    if (!excuseForm.excused_until || !excuseForm.excuse_reason) {
      toast.warning('Укажите время и причину');
      return;
    }
    setExcuseLoading(true);
    try {
      await timesheetsAPI.setExcuse(excuseModal.timesheet.id, excuseForm);
      toast.success('Разрешение на приход сохранено');
      setExcuseModal({ open: false, timesheet: null });
      setExcuseForm({ excused_until: '', excuse_reason: '' });
      await refetch();
    } catch (error: any) {
      toast.error(error?.message || 'Произошла ошибка');
    } finally {
      setExcuseLoading(false);
    }
  };

  const handleRemoveExcuse = async (timesheetId: number) => {
    if (!confirm('Удалить разрешение?')) return;
    try {
      await timesheetsAPI.removeExcuse(timesheetId);
      toast.success('Разрешение удалено');
      await refetch();
    } catch (error: any) {
      toast.error(error?.message || 'Произошла ошибка');
    }
  };

  const openExcuseModal = (ts: any) => {
    setExcuseModal({ open: true, timesheet: ts });
    // Pre-fill with existing excuse if any
    if (ts.excused_until) {
      setExcuseForm({
        excused_until: ts.excused_until.slice(0, 5),
        excuse_reason: ts.excuse_reason || '',
      });
    } else {
      // Default to planned start time + 2 hours
      const defaultTime = ts.planned_start_time
        ? (() => {
            const [h, m] = ts.planned_start_time.split(':').map(Number);
            const newH = Math.min(h + 2, 23);
            return `${String(newH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
          })()
        : '11:00';
      setExcuseForm({ excused_until: defaultTime, excuse_reason: '' });
    }
  };

  const openManualModal = (ts: any) => {
    setManualModal({ open: true, timesheet: ts });
    setManualForm({
      check_in_at: ts.first_in_at ? '' : (ts.planned_start_time?.slice(0, 5) ?? ''),
      check_out_at: ts.last_out_at ? '' : (ts.planned_end_time?.slice(0, 5) ?? ''),
      reason: '',
    });
  };

  const handleSaveManualAttendance = async () => {
    if (!manualModal.timesheet) return;

    if (!manualForm.check_in_at && !manualForm.check_out_at) {
      toast.warning('Укажите хотя бы одно время');
      return;
    }

    if (!manualForm.reason.trim()) {
      toast.warning('Укажите причину');
      return;
    }

    setManualLoading(true);
    try {
      await timesheetsAPI.manualAttendance(manualModal.timesheet.id, {
        check_in_at: manualForm.check_in_at || undefined,
        check_out_at: manualForm.check_out_at || undefined,
        reason: manualForm.reason.trim(),
      });
      toast.success('Ручная отметка сохранена');
      setManualModal({ open: false, timesheet: null });
      setManualForm({ check_in_at: '', check_out_at: '', reason: '' });
      await refetch();
    } catch (error: any) {
      const message = error?.errors
        ? Object.values(error.errors).flat()[0]
        : (error?.message || 'Произошла ошибка');
      toast.error(String(message));
    } finally {
      setManualLoading(false);
    }
  };

  const handleRecompute = async () => {
    if (!filters.date_from || !filters.date_to) {
      toast.warning('Выберите диапазон дат');
      return;
    }
    setRecomputing(true);
    try {
      await timesheetsAPI.recompute({
        date_from: filters.date_from,
        date_to: filters.date_to,
        employee_id: filters.employee_id || undefined,
      });
      await refetch();
      toast.success('Перерасчёт завершён');
    } catch (error) {
      toast.error('Ошибка при перерасчёте');
    } finally {
      setRecomputing(false);
    }
  };

  // Russian status labels with explanations
  const statusConfig: Record<string, { label: string; description: string; variant: string; icon: React.ReactNode }> = {
    present: { label: 'Пришёл', description: 'Сотрудник был на работе', variant: 'success', icon: <CheckCircle2 className="w-3 h-3" /> },
    working: { label: 'На смене', description: 'Сотрудник сейчас на рабочем месте', variant: 'success', icon: <Activity className="w-3 h-3 animate-pulse" /> },
    late: { label: 'Опоздал', description: 'Пришёл позже установленного времени', variant: 'warning', icon: <Clock className="w-3 h-3" /> },
    absent: { label: 'Отсутствовал', description: 'Нет отметки прихода', variant: 'destructive', icon: <UserX className="w-3 h-3" /> },
    no_checkout: { label: 'Нет ухода', description: 'Скан ухода не найден, время могло быть записано автоматически', variant: 'warning', icon: <AlertTriangle className="w-3 h-3" /> },
    no_checkin: { label: 'Нет прихода', description: 'Есть уход, но приход не зафиксирован', variant: 'warning', icon: <AlertTriangle className="w-3 h-3" /> },
    early_leave: { label: 'Ранний уход', description: 'Ушёл раньше установленного времени', variant: 'warning', icon: <Clock className="w-3 h-3" /> },
    no_show: { label: 'Неявка', description: 'Смена была назначена, но сотрудник не вышел', variant: 'destructive', icon: <UserX className="w-3 h-3" /> },
    pending: { label: 'Ожидается', description: 'Смена ещё не началась', variant: 'secondary', icon: <Clock className="w-3 h-3" /> },
    weekend: { label: 'Выходной', description: 'Выходной день', variant: 'secondary', icon: <Calendar className="w-3 h-3" /> },
    holiday: { label: 'Праздник', description: 'Официальный праздничный день', variant: 'secondary', icon: <Calendar className="w-3 h-3" /> },
    day_off: { label: 'Отгул', description: 'Назначенный выходной день', variant: 'secondary', icon: <Calendar className="w-3 h-3" /> },
    leave: { label: 'Отпуск', description: 'Официальный отпуск', variant: 'outline', icon: <Calendar className="w-3 h-3" /> },
    unpaid_leave: { label: 'Без оплаты', description: 'Неоплачиваемый отпуск', variant: 'outline', icon: <Calendar className="w-3 h-3" /> },
  };

  // Determine effective status - show "На смене" for people currently working
  const getEffectiveStatus = (ts: any) => {
    const isToday = ts.work_date === today;
    const hasCheckedIn = !!ts.first_in_at;
    const hasCheckedOut = !!ts.last_out_at;
    const isWorkingStatus = ['present', 'late'].includes(ts.status);

    // If today, checked in, not checked out, and status is present/late → show as "На смене"
    if (isToday && hasCheckedIn && !hasCheckedOut && isWorkingStatus) {
      return 'working';
    }
    return ts.status;
  };

  const getStatusBadge = (status: string) => {
    const config = statusConfig[status] || { label: status, variant: 'default', icon: null, description: '' };
    return (
      <span title={config.description}>
        <Badge variant={config.variant as any} className="gap-1">
          {config.icon}
          {config.label}
        </Badge>
      </span>
    );
  };

  const getNoteBadge = (note: string | null | undefined) => {
    if (!note) return null;

    const label = note.includes('Время ухода записано автоматически') || note.includes('Chiqish vaqti avtomatik')
      ? 'Автовыход'
      : note.includes('Скан ухода не найден') || note.includes('Ketishda urilmadi')
        ? 'Нет ухода'
        : 'Комментарий';

    return (
      <span
        className="inline-flex max-w-[160px] truncate rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
        title={note}
      >
        {label}
      </span>
    );
  };

  const getExcuseBadges = (ts: any) => {
    const items: Array<{ key: string; label: string; reason?: string; className: string }> = [];

    if (ts.excused_until) {
      items.push({
        key: 'arrival',
        label: `Приход до ${ts.excused_until.slice(0, 5)}`,
        reason: ts.excuse_reason || undefined,
        className: 'text-purple-700 bg-purple-50',
      });
    }

    if (ts.early_leave_excused_until) {
      items.push({
        key: 'early-leave',
        label: `Ранний уход до ${ts.early_leave_excused_until.slice(0, 5)}`,
        reason: ts.early_leave_excuse_reason || undefined,
        className: 'text-amber-700 bg-amber-50',
      });
    }

    if (items.length === 0) {
      return <span className="text-slate-300">—</span>;
    }

    return (
      <div className="flex flex-col items-center gap-1">
        {items.map((item) => (
          <div key={item.key} className="flex flex-col items-center">
            <span className={`font-medium px-2 py-1 rounded-lg text-xs ${item.className}`}>
              {item.label}
            </span>
            {item.reason && (
              <span className="text-xs text-slate-500 mt-0.5 max-w-[140px] truncate" title={item.reason}>
                {item.reason}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Format time nicely
  const formatTime = (dateString: string | null) => {
    return formatAttendanceTime(dateString, 'ru-RU');
  };

  // Format minutes to hours and minutes
  const formatMinutes = (minutes: number | null | undefined) => {
    if (!minutes || minutes <= 0) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours} ч ${mins} мин`;
    }
    return `${mins} мин`;
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      {/* Header */}
      <div className="page-toolbar">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Табель посещаемости</h1>
          <p className="text-slate-500 mt-1">Учёт рабочего времени сотрудников и отчёты</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRecompute} disabled={recomputing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${recomputing ? 'animate-spin' : ''}`} />
            Перерасчёт
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Экспорт
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">С даты</label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => {
                  setPage(1);
                  setFilters({ ...filters, date_from: e.target.value });
                }}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">По дату</label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => {
                  setPage(1);
                  setFilters({ ...filters, date_to: e.target.value });
                }}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Сотрудник</label>
              <select
                value={filters.employee_id}
                onChange={(e) => {
                  setPage(1);
                  setFilters({ ...filters, employee_id: e.target.value });
                }}
                className="w-full min-h-[2.75rem] rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Все сотрудники</option>
                {employees.map((emp: any) => (
                  <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button onClick={() => refetch()} className="w-full md:w-auto">
                <Search className="w-4 h-4 mr-2" />
                Поиск
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timesheets Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Дата</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Сотрудник</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Статус</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">
                    <div className="flex items-center justify-center gap-1">
                      <UserCheck className="w-4 h-4 text-green-600" />
                      Приход
                    </div>
                  </th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">
                    <div className="flex items-center justify-center gap-1">
                      <UserX className="w-4 h-4 text-orange-600" />
                      Уход
                    </div>
                  </th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Отработал</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Опоздание</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Сверхурочно</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">
                    <div className="flex items-center justify-center gap-1">
                      <ShieldCheck className="w-4 h-4 text-purple-600" />
                      Разрешение
                    </div>
                  </th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Действие</th>
                </tr>
              </thead>
              <tbody>
                {timesheets.map((ts: any) => {
                  const firstIn = formatTime(ts.first_in_at);
                  const lastOut = formatTime(ts.last_out_at);
                  const worked = formatMinutes(ts.worked_minutes);
                  const late = formatMinutes(ts.late_minutes);
                  const overtime = formatMinutes(ts.overtime_minutes);
                  const effectiveStatus = getEffectiveStatus(ts);

                  return (
                    <tr key={ts.id} className="border-b hover:bg-slate-50/70 transition">
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-800">
                          {new Date(ts.work_date).toLocaleDateString('ru-RU', {
                            day: 'numeric',
                            month: 'short',
                            weekday: 'short'
                          })}
                        </div>
                        {(ts.planned_start_time || ts.planned_end_time) && (
                          <div className="text-xs text-slate-400 font-mono mt-0.5">
                            {ts.planned_start_time?.slice(0, 5) ?? '??:??'}
                            {' – '}
                            {ts.planned_end_time?.slice(0, 5) ?? '??:??'}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-800">{ts.employee?.full_name}</div>
                        {ts.shift?.name && (
                          <div className="text-xs text-slate-500">{ts.shift.name}</div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col items-start gap-1">
                          {getStatusBadge(effectiveStatus)}
                          {getNoteBadge(ts.notes)}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {firstIn ? (
                          <span className="font-medium text-green-700 bg-green-50 px-2 py-1 rounded-lg">
                            {firstIn}
                          </span>
                        ) : ts.planned_start_time ? (
                          <span className="text-slate-400 text-sm font-mono" title="Плановое время">
                            {ts.planned_start_time.slice(0, 5)}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {lastOut ? (
                          <span className="font-medium text-orange-700 bg-orange-50 px-2 py-1 rounded-lg">
                            {lastOut}
                          </span>
                        ) : ts.planned_end_time ? (
                          <span className="text-slate-400 text-sm font-mono" title="Плановое время">
                            {ts.planned_end_time.slice(0, 5)}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {worked ? (
                          <span className="font-medium text-blue-700">{worked}</span>
                        ) : (
                          <span className="text-slate-300">0</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {late ? (
                          <span className="font-medium text-red-600 bg-red-50 px-2 py-1 rounded-lg">
                            {late}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {overtime ? (
                          <span className="font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg">
                            +{overtime}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      {/* Excuse column */}
                      <td className="py-3 px-4 text-center">
                        {getExcuseBadges(ts)}
                      </td>
                      {/* Action column */}
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {!ts.locked_for_payroll && (
                            <>
                              {ts.excused_until ? (
                                <span title="Удалить разрешение">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                                    onClick={() => handleRemoveExcuse(ts.id)}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </span>
                              ) : (
                                <span title="Дать разрешение на приход">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-7 px-2"
                                    onClick={() => openExcuseModal(ts)}
                                  >
                                    <ShieldCheck className="w-3 h-3" />
                                  </Button>
                                </span>
                              )}
                              <span title="Внести ручную отметку">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-sky-600 hover:text-sky-700 hover:bg-sky-50 h-7 px-2"
                                  onClick={() => openManualModal(ts)}
                                >
                                  <Clock className="w-3 h-3" />
                                </Button>
                              </span>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Pagination meta={meta} onPageChange={setPage} />

      {timesheets.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-800">Данные не найдены</h3>
          <p className="text-slate-500">Выберите диапазон дат и выполните поиск</p>
        </div>
      )}

      {/* Excuse Modal */}
      {excuseModal.open && excuseModal.timesheet && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-purple-50 px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-purple-900">Разрешение на приход</h3>
                <p className="text-sm text-purple-700">
                  {excuseModal.timesheet.employee?.full_name} — {new Date(excuseModal.timesheet.work_date).toLocaleDateString('ru-RU')}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setExcuseModal({ open: false, timesheet: null })}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Разрешить приход до
                </label>
                <Input
                  type="time"
                  value={excuseForm.excused_until}
                  onChange={(e) => setExcuseForm({ ...excuseForm, excused_until: e.target.value })}
                  className="w-full"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Например: 11:00 — сотрудник может прийти до этого времени, опоздание не засчитывается.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Причина
                </label>
                <textarea
                  value={excuseForm.excuse_reason}
                  onChange={(e) => setExcuseForm({ ...excuseForm, excuse_reason: e.target.value })}
                  className="w-full min-h-[80px] rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Врач, медосмотр, проблема с транспортом..."
                />
              </div>
              {excuseModal.timesheet.planned_start_time && (
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Плановый приход:</span>
                    <span className="font-medium">{excuseModal.timesheet.planned_start_time.slice(0, 5)}</span>
                  </div>
                  {excuseForm.excused_until && (
                    <div className="flex justify-between mt-1">
                      <span className="text-slate-600">С разрешением:</span>
                      <span className="font-medium text-purple-700">{excuseForm.excused_until}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="bg-slate-50 px-6 py-4 border-t flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setExcuseModal({ open: false, timesheet: null })}
                disabled={excuseLoading}
              >
                Отмена
              </Button>
              <Button
                onClick={handleSetExcuse}
                disabled={excuseLoading || !excuseForm.excused_until || !excuseForm.excuse_reason}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {excuseLoading ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {manualModal.open && manualModal.timesheet && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-sky-50 px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-sky-900">Ручная отметка прихода / ухода</h3>
                <p className="text-sm text-sky-700">
                  {manualModal.timesheet.employee?.full_name} — {new Date(manualModal.timesheet.work_date).toLocaleDateString('ru-RU')}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setManualModal({ open: false, timesheet: null })}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Приход
                  </label>
                  <Input
                    type="time"
                    value={manualForm.check_in_at}
                    onChange={(e) => setManualForm({ ...manualForm, check_in_at: e.target.value })}
                    className="w-full"
                  />
                  <p className="text-xs text-slate-500 mt-1">Оставьте пустым, если менять не нужно</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Уход
                  </label>
                  <Input
                    type="time"
                    value={manualForm.check_out_at}
                    onChange={(e) => setManualForm({ ...manualForm, check_out_at: e.target.value })}
                    className="w-full"
                  />
                  <p className="text-xs text-slate-500 mt-1">Оставьте пустым, если менять не нужно</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Причина
                </label>
                <textarea
                  value={manualForm.reason}
                  onChange={(e) => setManualForm({ ...manualForm, reason: e.target.value })}
                  className="w-full min-h-[90px] rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  placeholder="Терминал не работал, отключили электричество, внесено вручную..."
                />
              </div>

              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-600">План:</span>
                  <span className="font-medium">
                    {manualModal.timesheet.planned_start_time?.slice(0, 5) ?? '??:??'}
                    {' – '}
                    {manualModal.timesheet.planned_end_time?.slice(0, 5) ?? '??:??'}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  Внесённые времена сохраняются в локальном времени посещаемости и сохраняются при последующих перерасчётах.
                </p>
              </div>
            </div>
            <div className="bg-slate-50 px-6 py-4 border-t flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setManualModal({ open: false, timesheet: null })}
                disabled={manualLoading}
              >
                Отмена
              </Button>
              <Button
                onClick={handleSaveManualAttendance}
                disabled={manualLoading || (!manualForm.check_in_at && !manualForm.check_out_at) || !manualForm.reason.trim()}
                className="bg-sky-600 hover:bg-sky-700"
              >
                {manualLoading ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

